<template>
  <!-- Wrap in a fixed full-height page so the Stepper can anchor to it -->
  <q-page class="absolute-full column bg-grey-2">
    <div class="col-grow row justify-center overflow-hidden q-pa-sm">
      <div class="col-12 col-md-10 col-lg-8 full-height column">
        <!-- Custom game-stepper class allows us to target flexbox properties -->
        <q-stepper
          v-model="step"
          horizontal
          color="primary"
          animated
          flat
          bordered
          class="col-grow column no-wrap game-stepper bg-white"
        >
          <q-step :name="1" title="Game Matchup" icon="sports_baseball" :done="step > 1">
            <div class="q-gutter-md">
              <q-select
                v-model="awayTeam"
                :options="filteredTeams"
                option-label="name"
                label="Away Team"
                outlined
                use-input
                @filter="filterTeams"
                @new-value="createTeamOnTheFly"
                hint="Select existing or type to create"
              >
                <template v-slot:no-option>
                  <q-item clickable @click="manualCreateTeam('away')">
                    <q-item-section avatar>
                      <q-icon name="add_circle" color="positive" />
                    </q-item-section>
                    <q-item-section class="text-primary text-weight-bold">
                      Create team: "{{ currentSearch }}"
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>

              <div class="text-center text-weight-bold text-grey-6 text-h6 q-my-sm">VS</div>

              <q-select
                v-model="homeTeam"
                :options="filteredTeams"
                option-label="name"
                label="Home Team"
                outlined
                use-input
                @filter="filterTeams"
                @new-value="createTeamOnTheFly"
                hint="Select existing or type to create"
              >
                <template v-slot:no-option>
                  <q-item clickable @click="manualCreateTeam('home')">
                    <q-item-section avatar>
                      <q-icon name="add_circle" color="positive" />
                    </q-item-section>
                    <q-item-section class="text-primary text-weight-bold">
                      Create team: "{{ currentSearch }}"
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>
          </q-step>

          <q-step
            :name="2"
            title="Starting Lineups"
            icon="list_alt"
            :done="step > 2"
            class="column no-wrap full-height"
          >
            <!-- Tabs are pinned at the top -->
            <q-tabs
              v-model="lineupTab"
              dense
              class="text-grey shrink q-mb-sm"
              active-color="primary"
              indicator-color="primary"
              align="justify"
            >
              <q-tab name="visiting" :label="awayTeam?.name || 'Away'" />
              <q-tab name="home" :label="homeTeam?.name || 'Home'" />
            </q-tabs>

            <!-- relative-position allows absolute children to bind to it -->
            <q-tab-panels
              v-model="lineupTab"
              animated
              class="bg-transparent col-grow relative-position min-h-0"
            >
              <q-tab-panel
                v-for="role in ['visiting', 'home'] as ('visiting' | 'home')[]"
                :key="role"
                :name="role"
                class="q-pa-none absolute-full"
              >
                <!-- q-scroll-area takes over and guarantees a clean scrollbar! -->
                <q-scroll-area class="fit">
                  <div class="row q-col-gutter-md q-py-sm q-px-sm">
                    <!-- Editor Side -->
                    <div class="col-12 col-md-5">
                      <lineup-entry-edit
                        v-show="!isEditing"
                        v-model="showBatterEditorAdd"
                        :role="role"
                        @saved="onBatterSaved"
                      />
                    </div>

                    <!-- Lineup List Side -->
                    <div class="col-12 col-md-7">
                      <div class="bg-white border-all rounded-borders q-pa-sm shadow-1">
                        <!-- Your LineupList component remains completely unchanged -->
                        <lineup-list v-model="showBatterEditor" :role="role" />
                      </div>
                    </div>
                  </div>
                </q-scroll-area>
              </q-tab-panel>
            </q-tab-panels>
          </q-step>

          <!-- GLOBAL NAVIGATION SLOT: Pins to bottom of the Stepper always! -->
          <template v-slot:navigation>
            <q-stepper-navigation
              ref="navStepper"
              class="bg-grey-1 border-top q-pa-md shadow-up-2 shrink"
            >
              <div class="row justify-between items-center">
                <q-btn
                  v-if="step === 2"
                  @click="step = 1"
                  color="primary"
                  flat
                  label="Back"
                  icon="arrow_back"
                />
                <div v-else></div>

                <q-btn
                  v-if="step === 1"
                  @click="saveDraftAndProceed"
                  color="primary"
                  label="Next: Lineups"
                  icon-right="arrow_forward"
                  unelevated
                  :disable="!awayTeam || !homeTeam"
                />
                <q-btn
                  v-if="step === 2"
                  @click="startNewGame"
                  color="positive"
                  label="Play Ball!"
                  icon-right="sports_baseball"
                  unelevated
                />
              </div>
            </q-stepper-navigation>

            <q-menu
              v-model="isEditing"
              :target="navStepper?.$el"
              anchor="top left"
              self="bottom middle"
              :offset="[0, 8]"
            >
              <div style="width: 400px">
                <lineup-entry-edit
                  v-model="showBatterEditor"
                  :role="lineupTab"
                  @saved="onBatterSaved"
                />
              </div>
            </q-menu>
          </template>
        </q-stepper>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar, QStepperNavigation } from 'quasar'
import { useGameStore } from 'stores/game-store'
import { db } from 'src/models/db'
import type { Team, LineupEntry } from 'src/stores/base-types'

// Component Imports
import LineupEntryEdit from 'components/LineupEntryEdit.vue'
import LineupList from 'components/LineupList.vue'

const router = useRouter()
const $q = useQuasar()
const gameStore = useGameStore()

onMounted(async () => {
  allTeams.value = await db.teams.toArray()
  filteredTeams.value = allTeams.value
})

// --- UI State ---
const currentSearch = ref('')
const navStepper = ref<InstanceType<typeof QStepperNavigation> | null>(null)
const step = ref(1)
const lineupTab = ref<'visiting' | 'home'>('visiting')
const showBatterEditorAdd = ref(false)
const showBatterEditor = ref(false)
const showBatterMenu = ref(false)
const isEditing = computed({
  get() {
    return gameStore.editingLineupEntry !== null
  },
  set(value) {
    showBatterMenu.value = value
  },
})

// --- Team Selection State ---
const allTeams = ref<Team[]>([])
const filteredTeams = ref<Team[]>([])
const awayTeam = ref<Team | null>(null)
const homeTeam = ref<Team | null>(null)

// --- THE WATCHER: Syncs Quasar Tabs with Pinia ---
watch(lineupTab, (newRole) => {
  if (newRole === 'home') {
    gameStore.activeTeam = gameStore.teamHome
  } else {
    gameStore.activeTeam = gameStore.teamVisiting
  }
  // Clear the form if the user was mid-edit and clicked the other tab
  gameStore.clearEditingBatter()
})

// Notify User on Save
const onBatterSaved = () => {
  showBatterEditorAdd.value = false
  $q.notify({
    message: 'Lineup updated successfully!',
    color: 'positive',
    position: 'top',
    timeout: 1000,
  })
}

// Search filter for the dropdowns
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterTeams(val: string, update: any) {
  update(() => {
    currentSearch.value = val
    const needle = val.toLowerCase()
    filteredTeams.value = allTeams.value.filter((v) => v.name.toLowerCase().indexOf(needle) > -1)
  })
}

async function manualCreateTeam(type: 'away' | 'home') {
  const val = currentSearch.value

  if (val.length > 0) {
    const newTeam: Team = { name: val, players: [] }
    newTeam.id = await db.teams.add(newTeam)
    allTeams.value.push(newTeam)

    if (type === 'away') {
      awayTeam.value = newTeam
    } else {
      homeTeam.value = newTeam
    }

    currentSearch.value = ''
  }
}

// On-The-Fly Team Creation
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function createTeamOnTheFly(val: string, done: any) {
  if (val.length > 0) {
    const newTeam: Team = { name: val, players: [] }
    newTeam.id = await db.teams.add(newTeam)

    allTeams.value.push(newTeam)
    done(newTeam, 'add-unique')
  }
}
const populateLineup = (team: Team, targetLineup: LineupEntry[]) => {
  if (targetLineup.length === 0 && team.players.length > 0) {
    // Filter out players who are explicitly marked as inactive (isActive === false)
    // If isActive is undefined or true, they are kept in the list.
    const activePlayers = team.players.filter((p) => p.isActive !== false)

    activePlayers.forEach((player, i) => {
      const primaryBattingOrder = player.primaryBattingOrder ?? 0
      const batOrder = primaryBattingOrder !== 0 ? primaryBattingOrder : i + 1
      targetLineup.push({
        player,
        battingOrder: batOrder,
        startingPosition: player.primaryPosition ?? 13,
        substitutions: [],
      })
    })

    targetLineup.sort((a, b) => a.battingOrder - b.battingOrder)
  }
}

// --- DRAFT SAVING LOGIC ---
const saveDraftAndProceed = async () => {
  if (!awayTeam.value || !homeTeam.value) return

  gameStore.clearActiveTeams()
  gameStore.teamHome = homeTeam.value
  gameStore.teamVisiting = awayTeam.value

  gameStore.activeTeam = gameStore.teamVisiting
  lineupTab.value = 'visiting'

  populateLineup(gameStore.teamHome, gameStore.homeLineup || [])
  populateLineup(gameStore.teamVisiting, gameStore.visitingLineup || [])

  if (!gameStore.activeGameSummary) {
    const draftGameId = `game_${Date.now()}`
    gameStore.activeGameSummary = {
      gameId: draftGameId,
      homeScoreByInning: [],
      visitingScoreByInning: [],
      currentInning: 1,
      currentHalfInning: 'top',
      currentBatterId: 1,
      currentPitcherId: 1,
      isSetupComplete: false,
    }
  }

  const draftMetadata = {
    ...gameStore.activeGameSummary,
    teamHome: gameStore.teamHome,
    teamVisiting: gameStore.teamVisiting,
    homeLineup: gameStore.homeLineup,
    visitingLineup: gameStore.visitingLineup,
  }
  await db.games.put(JSON.parse(JSON.stringify(draftMetadata)))

  step.value = 2
}

const startNewGame = async () => {
  try {
    await gameStore.startNewGame()
    await router.push({ name: 'GameTracker' })
  } catch (error) {
    console.error('Failed to boot game in offline storage', error)
  }
}
</script>

<style scoped>
/* Stepper CSS overrides to enforce strict Flexbox hierarchy */
:deep(.game-stepper .q-stepper__content) {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

:deep(.game-stepper .q-stepper__step-content) {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

:deep(.game-stepper .q-stepper__step-inner) {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 16px;
  overflow: hidden; /* CRITICAL: Prevents the entire step from scrolling and trapping the tabs */
}

/* Ensure Quasar's internal tab-panel wrappers pass down the flex height */
:deep(.q-panel-parent) {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Helper Utilities */
.shrink {
  flex-shrink: 0;
}

.min-h-0 {
  min-height: 0;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}

.border-all {
  border: 1px solid #e0e0e0;
}
</style>
